import React, { useState, useRef, useEffect } from 'react';
import { PART_STATUSES, getPartStatus } from './roConstants';
import { tsToMs, fmtDate } from './roHelpers';

export default function PartsSection({
  truck, canManageParts, canViewCost, isClosed, partsMarkup,
  onAddPart, onUpdatePart, onDeletePart, onPartStatusChange,
  blockerPrompt, onSetBlocker, onDismissBlockerPrompt,
  clearBlockerPrompt, onClearBlocker, onDismissClearBlockerPrompt,
}) {
  const parts = truck.parts || [];

  const EMPTY_ADD = { description: '', partNumber: '', vendor: '', quantity: 1, cost: '', price: '', status: 'ordered', eta: '' };
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [statusMenuId, setStatusMenuId] = useState(null);
  const statusMenuRef = useRef(null);

  useEffect(() => {
    if (!statusMenuId) return;
    function h(e) { if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) setStatusMenuId(null); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [statusMenuId]);

  function calcPriceFromCost(cost) {
    if (!cost || isNaN(parseFloat(cost)) || !partsMarkup) return '';
    return (parseFloat(cost) * (1 + partsMarkup / 100)).toFixed(2);
  }

  function handleAddChange(field, value) {
    setAddForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'cost') next.price = calcPriceFromCost(value);
      return next;
    });
  }

  function handleAddSubmit(e) {
    e.preventDefault();
    if (!addForm.description.trim()) return;
    onAddPart(addForm);
    setAddForm(EMPTY_ADD);
  }

  function startEdit(part) {
    setEditId(part.id);
    setEditForm({
      description: part.description,
      partNumber:  part.partNumber  || '',
      vendor:      part.vendor      || '',
      quantity:    part.quantity,
      cost:        part.cost,
      price:       part.price,
      status:      part.status,
      eta:         part.eta ? new Date(tsToMs(part.eta)).toISOString().split('T')[0] : '',
    });
  }

  function handleEditChange(field, value) {
    setEditForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'cost') next.price = calcPriceFromCost(value) || prev.price;
      return next;
    });
  }

  function handleEditSave() {
    if (!editForm.description?.trim()) return;
    onUpdatePart(editId, editForm);
    setEditId(null);
  }

  const subtotal  = parts.reduce((s, p) => s + (parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1), 0);
  const costTotal = parts.reduce((s, p) => s + (parseFloat(p.cost)  || 0) * (parseFloat(p.quantity) || 1), 0);
  const markupPct = costTotal > 0 ? Math.round((subtotal - costTotal) / costTotal * 100) : null;

  return (
    <div className="ro-parts-wrap">

      {/* Blocker prompts */}
      {blockerPrompt && (
        <div className="ro-parts-prompt">
          <span>Set a parts blocker on this RO?</span>
          <button onClick={onSetBlocker}>Yes, set it</button>
          <button onClick={onDismissBlockerPrompt}>Skip</button>
        </div>
      )}
      {clearBlockerPrompt && (
        <div className="ro-parts-prompt">
          <span>All parts received. Clear the parts blocker?</span>
          <button onClick={onClearBlocker}>Yes, clear it</button>
          <button onClick={onDismissClearBlockerPrompt}>Not yet</button>
        </div>
      )}

      {/* Table */}
      {parts.length > 0 ? (
        <>
          <div className="ro-parts-table-scroll">
            <table className="ro-parts-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Part #</th>
                  <th>Vendor</th>
                  <th className="ro-th--num">Qty</th>
                  {canViewCost && <th className="ro-th--num">Cost</th>}
                  <th className="ro-th--num">Price</th>
                  <th>Status</th>
                  <th>ETA</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {parts.map(part => editId === part.id ? (
                  <tr key={part.id} className="ro-part-row ro-part-row--editing">
                    <td>
                      <input className="ro-part-input ro-part-input--wide" value={editForm.description}
                        onChange={e => handleEditChange('description', e.target.value)} autoFocus />
                    </td>
                    <td><input className="ro-part-input" value={editForm.partNumber} onChange={e => handleEditChange('partNumber', e.target.value)} /></td>
                    <td><input className="ro-part-input" value={editForm.vendor} onChange={e => handleEditChange('vendor', e.target.value)} /></td>
                    <td>
                      <input className="ro-part-input ro-part-input--num" type="number" min="0.01" step="0.01"
                        value={editForm.quantity} onChange={e => handleEditChange('quantity', e.target.value)} />
                    </td>
                    {canViewCost && (
                      <td>
                        <input className="ro-part-input ro-part-input--num" type="number" min="0" step="0.01"
                          value={editForm.cost} onChange={e => handleEditChange('cost', e.target.value)} />
                      </td>
                    )}
                    <td>
                      <input className="ro-part-input ro-part-input--num" type="number" min="0" step="0.01"
                        value={editForm.price} onChange={e => handleEditChange('price', e.target.value)} />
                    </td>
                    <td>
                      <select className="ro-part-select" value={editForm.status} onChange={e => handleEditChange('status', e.target.value)}>
                        {PART_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <input className="ro-part-input" type="date" value={editForm.eta}
                        onChange={e => handleEditChange('eta', e.target.value)} />
                    </td>
                    <td className="ro-part-actions">
                      <button className="ro-part-action-btn ro-part-action-btn--save" onClick={handleEditSave} title="Save">✓</button>
                      <button className="ro-part-action-btn ro-part-action-btn--cancel" onClick={() => setEditId(null)} title="Cancel">✕</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={part.id} className={`ro-part-row${part.status === 'received' ? ' ro-part-row--received' : ''}`}>
                    <td className="ro-part-desc">{part.description}
                      {part.notes && <span className="ro-part-note">{part.notes}</span>}
                    </td>
                    <td className="ro-part-sec">{part.partNumber || <span className="ro-muted">—</span>}</td>
                    <td className="ro-part-sec">{part.vendor    || <span className="ro-muted">—</span>}</td>
                    <td className="ro-td--num">{part.quantity}</td>
                    {canViewCost && <td className="ro-td--num ro-td--cost">${(parseFloat(part.cost) || 0).toFixed(2)}</td>}
                    <td className="ro-td--num ro-td--price">${(parseFloat(part.price) || 0).toFixed(2)}</td>
                    <td>
                      <div className="ro-part-status-wrap" ref={statusMenuId === part.id ? statusMenuRef : null}>
                        <button
                          className="ro-part-status-chip"
                          style={{ color: getPartStatus(part.status).color, background: getPartStatus(part.status).bg }}
                          onClick={() => canManageParts && !isClosed && setStatusMenuId(id => id === part.id ? null : part.id)}
                        >
                          {getPartStatus(part.status).label}
                        </button>
                        {statusMenuId === part.id && (
                          <div className="ro-part-status-menu">
                            {PART_STATUSES.filter(s => s.value !== part.status).map(s => (
                              <button key={s.value} className="ro-part-status-opt"
                                style={{ color: s.color }}
                                onClick={() => { onPartStatusChange(part.id, s.value); setStatusMenuId(null); }}
                              >{s.label}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="ro-td--eta">{fmtDate(part.eta)}</td>
                    <td className="ro-part-actions">
                      {canManageParts && !isClosed && (
                        <>
                          <button className="ro-part-action-btn ro-part-action-btn--edit" onClick={() => startEdit(part)} title="Edit">✎</button>
                          <button className="ro-part-action-btn ro-part-action-btn--del" onClick={() => onDeletePart(part.id)} title="Delete">×</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ro-parts-totals">
            <span className="ro-parts-total-label">Parts subtotal</span>
            <span className="ro-parts-total-val">${subtotal.toFixed(2)}</span>
            {canViewCost && markupPct !== null && (
              <span className="ro-parts-markup-tag">{markupPct}% markup</span>
            )}
          </div>
        </>
      ) : (
        <p className="ro-muted" style={{ margin: 0 }}>
          {canManageParts ? 'No parts on this RO. Add a part below.' : 'No parts tracked.'}
        </p>
      )}

      {/* Add part form */}
      {canManageParts && !isClosed && (
        <form className="ro-parts-add-form" onSubmit={handleAddSubmit}>
          <div className="ro-parts-add-row">
            <input className="ro-part-add-input ro-part-add-input--desc"
              value={addForm.description} onChange={e => handleAddChange('description', e.target.value)}
              placeholder="Description *" required />
            <input className="ro-part-add-input"
              value={addForm.partNumber} onChange={e => handleAddChange('partNumber', e.target.value)}
              placeholder="Part #" />
            <input className="ro-part-add-input"
              value={addForm.vendor} onChange={e => handleAddChange('vendor', e.target.value)}
              placeholder="Vendor" />
            <input className="ro-part-add-input ro-part-add-input--num"
              type="number" min="0.01" step="0.01"
              value={addForm.quantity} onChange={e => handleAddChange('quantity', e.target.value)}
              placeholder="Qty" />
            {canViewCost && (
              <input className="ro-part-add-input ro-part-add-input--num"
                type="number" min="0" step="0.01"
                value={addForm.cost} onChange={e => handleAddChange('cost', e.target.value)}
                placeholder="Cost $" />
            )}
            <input className="ro-part-add-input ro-part-add-input--num"
              type="number" min="0" step="0.01"
              value={addForm.price} onChange={e => handleAddChange('price', e.target.value)}
              placeholder="Price $" />
            <select className="ro-part-add-select"
              value={addForm.status} onChange={e => handleAddChange('status', e.target.value)}>
              {PART_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <input className="ro-part-add-input" type="date"
              value={addForm.eta} onChange={e => handleAddChange('eta', e.target.value)} />
            <button className="ro-part-add-btn" type="submit">+ Add</button>
          </div>
        </form>
      )}
    </div>
  );
}
